import { EditCategoryWidget } from '@modules/common/components/EditCategoryWidget';
import { EditReasonCategoryWidget } from '@modules/common/components/EditReasonCategoryWidget';
import {
  EscHotKeyIcon,
  ReturnHotKeyIcon,
} from '@modules/common/components/HotKeyIcons';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { Category } from '@modules/common/types';
import { mapCategoryToEdgeCategory } from '@modules/common/utils';
import { useUpdateEditedPredictions } from '@modules/feedback/hooks';
import { useFeedbackOnFeedbackStore } from '@modules/feedback/store';
import { FeedbackOnFeedbackActionType } from '@modules/feedback/store/feedbackOnFeedbackActionType';
import { trackFeedbackOnFeedbackV2Events } from '@modules/feedback/utils';
import { trackTaxonomyEvent } from '@modules/taxonomy/helpers/trackTaxonomyEvents';
import { useUpdateReason } from '@modules/taxonomy/hooks';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Divider, Paper, Stack, Typography } from '@mui/material';
import { ManipulationType } from 'generated/graphql';
import { useCallback, useEffect, useState } from 'react';
import { Feedback, FeedbackLabelType } from 'v2/core/entities/feedback';
import { ReasonOrEntityLabelType } from 'v2/core/entities/feedback/labels';
import { useGetUser } from 'v2/core/services/user';

interface EditCategoryFormProps {
  label: ReasonOrEntityLabelType;
  feedback: Feedback;
  category: Category;
  onClose: () => void;
}

export const EditCategoryForm = ({
  label,
  feedback,
  category,
  onClose,
}: EditCategoryFormProps) => {
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();
  const { closeModal } = useMuiModalContext();
  const { showSuccessNotification, showErrorNotification } =
    useNotificationContext();
  const dispatch = useFeedbackOnFeedbackStore(({ dispatch }) => dispatch);
  const recordInEditMode = useFeedbackOnFeedbackStore(
    ({ recordInEditMode }) => recordInEditMode,
  );
  const [selectedCategory, setSelectedCategory] = useState<
    Category | undefined
  >(category);

  const [
    shouldUpdateTrackedKeywordCategory,
    setShouldUpdateTrackedKeywordCategory,
  ] = useState(false);

  const { updateEditedPredictions } = useUpdateEditedPredictions(feedback.id);

  const { updateReason, loading: updateReasonInProgress } = useUpdateReason({
    onCompleted: () => {
      showSuccessNotification({
        contentComponent: () => (
          <Stack spacing={1} mt={-0.5}>
            <Typography variant="subtitle1">Thanks for the edit!</Typography>
            <Box component="p">
              <Typography component="span" variant="body2">
                <Typography
                  component="span"
                  variant="body2"
                  color="text.primary"
                >
                  “{label.displayName}”
                </Typography>{' '}
                category has been changed to{' '}
                <Typography
                  component="span"
                  variant="body2"
                  color="text.primary"
                >
                  {selectedCategory}
                </Typography>
                . The category change may take up to 60 mins to reflect on your
                organization's Taxonomy.
              </Typography>
            </Box>
          </Stack>
        ),
        props: {},
      });
      if (selectedCategory) {
        trackTaxonomyEvent({
          eventName: 'Edit Reason Category',
          id: label.id,
          oldCategory: category,
          newCategory: selectedCategory,
          displayName: label.displayName,
        });
      }
      onClose();
    },
    onError: () => {
      showErrorNotification({
        contentComponent: 'Failed to edit reason category.',
        props: {},
      });
    },
  });

  const onEditCategory = async (event: React.FormEvent) => {
    event.preventDefault();

    if (label.type === FeedbackLabelType.Reason && selectedCategory) {
      await updateReason({
        variables: {
          userID,
          tenantID,
          id: label.id,
          category: mapCategoryToEdgeCategory(selectedCategory),
        },
      });
      return;
    }

    if (label.type === FeedbackLabelType.Entity) {
      dispatch({
        type: FeedbackOnFeedbackActionType.SET_RECORD_IN_EDIT_MODE,
        payload: {
          id: feedback.id,
          editPredictions: [
            ...(recordInEditMode?.editPredictions ?? []),
            {
              type: FeedbackLabelType.Entity,
              manipulationType: ManipulationType.Add,
              oldCategory: category,
              category: selectedCategory,
              prediction: label,
            },
          ],
        },
      });
      setShouldUpdateTrackedKeywordCategory(true);
    }
  };

  const onAfterEditedPredictions = useCallback(() => {
    trackFeedbackOnFeedbackV2Events({
      eventName: 'Prediction Edited',
      feedbackID: feedback.id,
      toggleLocation: 'Feedback Detail View',
      predictionType: label?.type === 'REASON' ? 'Reason' : 'Tracked Keyword',
      predictionName: label?.displayName,
      edit: {
        oldCategory: category,
        newCategory: selectedCategory,
      },
    });
    showSuccessNotification({
      contentComponent: () => (
        <Stack spacing={1} mt={-0.5}>
          <Typography variant="subtitle1">Thanks for the edit!</Typography>
          <Typography variant="body2">
            We'll use it to level up Enterpret's analytics. This change may take
            up to 60 mins to reflect on your organization's Taxonomy.
          </Typography>
        </Stack>
      ),
      props: {},
    });
  }, [
    category,
    feedback.id,
    label?.displayName,
    label?.type,
    selectedCategory,
    showSuccessNotification,
  ]);

  useEffect(() => {
    const updateTrackedKeywordCategory = async () => {
      await updateEditedPredictions({
        onAfterEditedPredictions,
      });
      onClose();
    };

    if (shouldUpdateTrackedKeywordCategory) {
      setShouldUpdateTrackedKeywordCategory(false);
      updateTrackedKeywordCategory();
    }
  }, [
    onAfterEditedPredictions,
    onClose,
    shouldUpdateTrackedKeywordCategory,
    updateEditedPredictions,
  ]);

  const onCancel = () => {
    onClose();
    closeModal();
  };

  return (
    <Paper
      sx={{
        borderRadius: 1,
      }}
    >
      <Stack px={1} pt={1} pb={2} spacing={2}>
        <Typography variant="subtitle1">Select a Category</Typography>
        {label.type === 'REASON' ? (
          <EditReasonCategoryWidget
            fullWidth
            iconSize={20}
            activeReasonName={label.displayName}
            selectedCategory={selectedCategory}
            updateSelectedCategory={setSelectedCategory}
          />
        ) : (
          <EditCategoryWidget
            fullWidth
            iconSize={20}
            selectedCategory={selectedCategory}
            updateSelectedCategory={setSelectedCategory}
          />
        )}
        <Divider />
        <Stack
          direction="row"
          component="form"
          spacing={1}
          justifyContent="flex-end"
          onSubmit={onEditCategory}
        >
          <Button
            variant="outlined"
            onClick={onCancel}
            endIcon={<EscHotKeyIcon />}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            disabled={
              !selectedCategory || selectedCategory === Category.Uncategorized
            }
            type="submit"
            loading={updateReasonInProgress}
            endIcon={<ReturnHotKeyIcon />}
          >
            Change
          </LoadingButton>
        </Stack>
      </Stack>
    </Paper>
  );
};
